<script setup lang="ts">

import Decimal from "decimal.js";
import useBackend from "@/composition/useBackend";
import { formatting, useProcesses } from "@hantera/portal-app";
import InventoryLabel from "@/components/Order/Deliveries/OrderLine/InventoryLabel.vue";
import { DateOnly } from "@hantera/design-system";

const props = defineProps<{
  skuNumber: string
}>()

const backend = useBackend()
const procs = useProcesses()

const response = await backend.fetch(`/core/resources/actors/sku/${props.skuNumber}`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify([{
    type: 'calculateAvailableStock',
    body: {
      // TODO Support allocations
      inventoryKeys: []
    }
  }])
})

const inventories: Record<string, {
  physicalQuantity: Decimal
  incoming: {
    date: Date
    available: Decimal
  }[]
}> = {}

if (!response.ok) {
  procs.error({
    title: 'Unable to fetch inventory',
    message: await response.text()
  })
} else {
  const availableStock = (await response.json()).data.calculateAvailableStock as Record<string, {
    unallocated: {
      physicalQuantity: Decimal
      incomingStock: {
        incomingStockId: string
        availableTotalQuantity: Decimal
        expectedOn: DateOnly
      }[]
    }
  }>
  // TODO Sum up allocations as well

  for (const inventoryKey in availableStock) {

    let physicalQuantity = availableStock[inventoryKey].unallocated.physicalQuantity
    let now = new Date().getTime()

    const incomingDates = availableStock[inventoryKey].unallocated.incomingStock.reduce((acc, current) => {

      const year = current.expectedOn.year
      const month = current.expectedOn.month
      const day = current.expectedOn.day

      const date = new Date(year, month, day).getTime()

      if (date <= now) {
        physicalQuantity = physicalQuantity.add(current.availableTotalQuantity)
        return acc
      }

      let available = (acc.get(date) ?? new Decimal(0)).add(current.availableTotalQuantity)
      acc.set(date, available)

      return acc

    }, new Map<number, Decimal>())
    const incomingDatesArray = Array.from(incomingDates.keys()).map(key => {
      const available: Decimal = incomingDates.get(key) ?? new Decimal(0)

      return {
        date: new Date(key),
        available
      }
    }).sort((a, b) => a.date.getTime() - b.date.getTime())

    inventories[inventoryKey] = {
      physicalQuantity: physicalQuantity,
      incoming: incomingDatesArray
    }
  }
}


</script>

<template>
  <div
    
  >
    <div class="box-content-label">
      Inventory
    </div>
    
    <table>
      <thead>
      <tr>
        <th>Inventory</th>
        <th>Date</th>
        <th class="available">Available</th>
      </tr>
      </thead>
      <tbody>
      <template
        v-for="(stock, inventoryKey) in inventories"
        :key="inventoryKey"
      >
      <tr>
        <td><InventoryLabel :inventory-key="inventoryKey" /></td>
        <td>{{ formatting.date(new Date()) }}</td>
        <td class="available">{{ formatting.number(stock.physicalQuantity) }}</td>
      </tr>
      <tr
        v-for="incomingStock in stock.incoming"
        :key="incomingStock.date.getTime()"
      >
        <td>&nbsp;</td>
        <td>{{ formatting.date(incomingStock.date) }}</td>
        <td class="available">{{ formatting.number(incomingStock.available) }}</td>
      </tr>
      </template>
      </tbody>
    </table>
    
    
  </div>
</template>

<style scoped>
table {
  display: grid;
  grid-template-columns: repeat(3, minmax(max-content, auto));
  grid-auto-rows: max-content;
  column-gap: 5px;
  row-gap: 2px;
  padding: 0 5px;
  width: 100%;
}

thead,
tbody,
tr {
  display: contents;
}

.available {
  text-align: right;
}
</style>
